import { Paragraph, TextRun, Table, TableRow, TableCell, TableLayoutType, BorderStyle, WidthType } from 'docx'
import { CVData } from 'types/printInterfaces'
import { languageLevels } from 'utils/utils'
import _ from 'lodash'
import { contentWidth, fontSize } from './pageLayout'
import { LanguagesData } from 'api/UtilsAPI'
import { TFunction } from 'i18next'

/** Number of language table columns, the same as in the PDF. */
const languageColumns = 3

/** Width of each language table column. */
const languageColumnWidth = contentWidth / languageColumns

/** Language level legend, from weakest to strongest like in the PDF. */
const languageLegend: Array<[string, string]> = [
  ['A2', 'language.basic'],
  ['B1', 'language.moderate'],
  ['B2', 'language.good'],
  ['C1', 'language.excellent'],
  ['C2', 'language.native'],
]

/**
 * Generates a language section table for the given CV data.
 *
 * @param {CVData} data - The CV data object.
 * @param {object} languages - The language object.
 * @param {function} t - The translation function.
 * @return {Table} - The generated language section table.
 * @notExported
 */
const language = (data: CVData, languages: LanguagesData | undefined, t: TFunction): Table => {
  // return language section

  const borders = {
    top: {
      style: BorderStyle.NONE,
      size: 0,
      color: '#FF0000',
    },
    bottom: {
      style: BorderStyle.NONE,
      size: 0,
      color: '#FF0000',
    },
    left: {
      style: BorderStyle.NONE,
      size: 0,
      color: '#FF0000',
    },
    right: {
      style: BorderStyle.NONE,
      size: 0,
      color: '#FF0000',
    },
  }

  // languages from the strongest to the weakest in three columns, like in the PDF
  const languageCells: TableCell[] = _.orderBy(data.languages, [item => item.level], ['desc']).map(
    language =>
      new TableCell({
        children: [
          new Paragraph({
            keepLines: true,
            style: 'CaleoNormal',
            children: [
              new TextRun({
                text: languages?.[language.languageCode] ?? '',
                bold: true,
              }),
              new TextRun({
                text: language.level ? t(languageLevels()[language.level - 1].label) : '',
                size: fontSize.small,
                break: 1,
              }),
            ],
            spacing: { after: 100 },
          }),
        ],
        width: {
          size: languageColumnWidth,
          type: WidthType.DXA,
        },
        borders,
      })
  )

  const languageRows: TableRow[] = []
  for (let i = 0; i < languageCells.length; i += languageColumns) {
    const rowCells = languageCells.slice(i, i + languageColumns)
    while (rowCells.length < languageColumns) {
      rowCells.push(new TableCell({ children: [], width: { size: languageColumnWidth, type: WidthType.DXA }, borders }))
    }
    languageRows.push(new TableRow({ cantSplit: true, children: rowCells }))
  }

  languageRows.push(
    new TableRow({
      cantSplit: true,
      children: [
        new TableCell({
          children: [
            new Paragraph({
              keepLines: true,
              children: languageLegend.flatMap(([code, key], index) => [
                ...(index > 0 ? [new TextRun({ text: '|' })] : []),
                new TextRun({ text: `  ${code} - ${t(key)}  ` }),
              ]),
              style: 'GrayText',
            }),
          ],
          borders,
          columnSpan: languageColumns,
        }),
      ],
    })
  )

  return new Table({
    rows: languageRows,
    // without explicit widths docx defaults to a 100 twip wide table
    width: { size: contentWidth, type: WidthType.DXA },
    layout: TableLayoutType.FIXED,
    columnWidths: Array(languageColumns).fill(languageColumnWidth),
  })
}

export default language
